import React, { useMemo } from 'react';
import { StackedBarChart } from '../ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportChartDataToCSV, exportToCSV } from '../../utils/exportToCSV';

/**
 * Distribution Stack Bar Widget Component
 * Modernized replacement for MultiStackBar_Dist_List.js
 * Displays commission distribution with tier breakdown
 * Most complex widget - handles special commission data
 */
function DistStackBarWidget({ data }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Extract legend/summary data
    const summaryData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            if (apiData[0].legend && apiData[0].legend.length > 0) {
                return {
                    total: apiData[0].legend[0]?.legendValue || 0,
                    totalLabel: apiData[0].legend[0]?.legendText || 'Total',
                    breakdown: apiData[0].legend[1]?.legendValue || 0,
                    breakdownLabel: apiData[0].legend[1]?.legendText || 'Breakdown'
                };
            }
            return null;
        } catch (err) {
            return null;
        }
    }, [apiData]);

    // Transform API data to Chart.js format
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const columnData = apiData[0].columnDataSet1;
            if (!columnData || columnData.length === 0) return null;

            // Get labels from first dataset
            const labels = columnData[0].labels;

            // Build datasets with commission-specific colors
            const colors = {
                'PPBM': '#f19f57',      // brand-accent
                'SPBM': '#313131',      // brand-dark
                'Override': '#3b82f6',  // info blue
                'Tier 1': '#10b981',    // green
                'Tier 2': '#f59e0b',    // warning
                'Tier 3': '#ef4444'     // danger
            };

            const datasets = columnData.map((dataset, index) => {
                const label = dataset.label || `Series ${index + 1}`;
                const color = colors[label] || colors['PPBM'];

                return {
                    label: label,
                    data: dataset.data,
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1
                };
            });

            return {
                labels,
                datasets
            };
        } catch (err) {
            console.error('Error transforming distribution chart data:', err);
            return null;
        }
    }, [apiData, data]);

    // Transform to table format with commission breakdown
    const tableData = useMemo(() => {
        if (!chartData) return [];

        return chartData.labels.map((label, index) => {
            const row = { 
                month: label,
                total: 0
            };
            
            chartData.datasets.forEach((dataset, dsIndex) => {
                const value = dataset.data[index] || 0;
                row[dataset.label] = value;
                row.total += value;
            });

            return row;
        });
    }, [chartData]);

    // Table columns with currency formatting
    const columns = useMemo(() => {
        if (!chartData) return [];

        const cols = [
            {
                key: 'month',
                label: data.XlabelString || 'Month',
                sortable: true
            }
        ];

        chartData.datasets.forEach((dataset) => {
            cols.push({
                key: dataset.label,
                label: dataset.label,
                sortable: true,
                format: (val) => {
                    if (typeof val === 'number') {
                        return `$${val.toLocaleString(undefined, { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                        })}`;
                    }
                    return val || '$0.00';
                }
            });
        });

        cols.push({
            key: 'total',
            label: 'Total',
            sortable: true,
            format: (val) => {
                if (typeof val === 'number') {
                    return `$${val.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                    })}`;
                }
                return val || '$0.00';
            }
        });

        return cols;
    }, [chartData, data]);

    // Export handler with proper formatting
    const handleExport = () => {
        if (tableData.length > 0) {
            exportToCSV(tableData, data.title || 'commission-data', columns);
        } else if (chartData) {
            exportChartDataToCSV(chartData, data.title || 'commission-chart-data');
        }
    };

    // Custom chart options for commission display
    const chartOptions = {
        scales: {
            x: {
                title: {
                    display: true,
                    text: data.XlabelString || 'Month'
                }
            },
            y: {
                title: {
                    display: true,
                    text: data.YlabelString || 'Commission ($)'
                },
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        return '$' + value.toLocaleString();
                    }
                }
            }
        },
        plugins: {
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += '$' + context.parsed.y.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            });
                        }
                        return label;
                    }
                }
            },
            legend: {
                display: true,
                position: 'top'
            }
        }
    };

    // Chart component with summary
    const chartComponent = chartData ? (
        <div className="space-y-4">
            {/* Summary Cards */}
            {summaryData && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-brand-light-2 rounded-lg p-4 border border-brand-dark/10">
                        <p className="text-sm font-medium text-brand-dark/70">
                            {summaryData.totalLabel}
                        </p>
                        <p className="text-2xl font-bold text-brand-dark mt-1">
                            ${parseFloat(summaryData.total || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    </div>
                    <div className="bg-brand-light-2 rounded-lg p-4 border border-brand-dark/10">
                        <p className="text-sm font-medium text-brand-dark/70">
                            {summaryData.breakdownLabel}
                        </p>
                        <p className="text-2xl font-bold text-brand-accent mt-1">
                            ${parseFloat(summaryData.breakdown || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    </div>
                </div>
            )}

            {/* Stacked Bar Chart */}
            <StackedBarChart
                datasets={chartData.datasets}
                labels={chartData.labels}
                height={400}
                options={chartOptions}
            />
        </div>
    ) : null;

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={chartComponent}
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination={{ enabled: true, itemsPerPage: 12 }}
                        sortable={true}
                        searchable={true}
                    />
                ) : null
            }
            onExport={handleExport}
            showToggle={true}
            showExport={true}
        />
    );
}

export default DistStackBarWidget;
